<template>
  <section class="mx-auto flex min-h-[70vh] max-w-5xl flex-col gap-8 px-6 py-16">
    <div v-if="post" class="rounded-3xl border border-white/10 bg-midnight-800/60 p-6 shadow-glow">
      <div class="flex items-center gap-3">
        <div class="h-12 w-12 overflow-hidden rounded-2xl border border-white/10 bg-midnight-900/70">
          <img
            v-if="authorAvatar"
            :src="authorAvatar"
            alt="Author avatar"
            class="h-full w-full object-cover"
            referrerpolicy="no-referrer"
            crossorigin="anonymous"
          />
          <div v-else class="flex h-full w-full items-center justify-center text-sm font-semibold text-steel-200">
            {{ authorInitials }}
          </div>
        </div>
        <div>
          <p class="text-sm font-semibold text-white">{{ authorName }}</p>
          <router-link class="text-xs text-steel-400 hover:text-white" :to="`/u/${authorHandle}`">
            @{{ authorHandle }}
          </router-link>
        </div>
        <span class="ml-auto text-xs text-steel-400">{{ createdLabel }}</span>
      </div>

      <div class="mt-5 overflow-hidden rounded-3xl border border-white/10 bg-midnight-900/70">
        <div class="relative aspect-square w-full" style="touch-action: pan-y;" @touchstart="handleSwipeStart" @touchend="handleSwipeEnd">
          <transition :name="mediaDirection === 'prev' ? 'media-swipe-prev' : 'media-swipe-next'" mode="out-in">
            <img
              :key="postImageUrl"
              :src="postImageUrl"
              alt="Post"
              class="h-full w-full object-cover"
              :style="{ objectPosition: postImagePosition }"
            />
          </transition>
          
          <!-- Prev button (desktop only) -->
          <button
            v-if="post.mediaUrls?.length > 1"
            type="button"
            class="hidden md:flex absolute left-3 top-1/2 -translate-y-1/2 h-10 w-10 items-center justify-center rounded-full border border-white/30 bg-midnight-900/70 text-white hover:border-white hover:bg-midnight-900/90 transition z-20"
            @click.stop="navigateToPrevious"
          >
            <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          
          <!-- Next button (desktop only) -->
          <button
            v-if="post.mediaUrls?.length > 1"
            type="button"
            class="hidden md:flex absolute right-3 top-1/2 -translate-y-1/2 h-10 w-10 items-center justify-center rounded-full border border-white/30 bg-midnight-900/70 text-white hover:border-white hover:bg-midnight-900/90 transition z-20"
            @click.stop="navigateToNext"
          >
            <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
          
          <!-- Dot indicators -->
          <div v-if="post.mediaUrls?.length > 1" class="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1">
            <button
              v-for="(url, idx) in post.mediaUrls"
              :key="idx"
              type="button"
              class="h-1 rounded-full transition-all"
              :class="idx === post.mediaIndex ? 'w-6 bg-white' : 'w-1 bg-white/40'"
              @click="setMediaIndex(idx)"
            />
          </div>
        </div>
      </div>

      <p v-if="post.caption" class="mt-4 text-sm text-steel-200/90">
        {{ post.caption }}
      </p>
      <p v-if="post.description" class="mt-2 text-sm text-steel-400">
        {{ post.description }}
      </p>
      <p v-if="post.modifications" class="mt-2 text-sm text-steel-400">
        Mods: {{ post.modifications }}
      </p>
      <div v-if="post.engine || post.hp_stock || post.hp_tuned || post.year" class="mt-2 text-sm text-steel-400">
        <p v-if="post.engine">Engine: {{ post.engine }}</p>
        <p v-if="post.hp_stock">HP Stock: {{ post.hp_stock }}</p>
        <p v-if="post.hp_tuned">HP Tuned: {{ post.hp_tuned }}</p>
        <p v-if="post.year">Year: {{ post.year }}</p>
      </div>

      <div class="mt-4 flex items-center gap-3">
        <button
          type="button"
          class="flex items-center gap-2 text-xs uppercase tracking-[0.3em]"
          :class="postIsLiked ? 'text-rose-500' : 'text-white'"
          @click="togglePostLike"
        >
          <svg class="h-5 w-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path
              d="M12 21s-6.7-4.3-9.3-7.6C.5 10.8 1 7.6 3 5.7c2-2 5.2-1.6 7 0l2 1.9 2-1.9c1.8-1.6 5-2 7 0 2 1.9 2.5 5.1.3 7.7C18.7 16.7 12 21 12 21z"
            />
          </svg>
          {{ postLikeCount }}
        </button>
        <button
          type="button"
          class="text-xs uppercase tracking-[0.3em] text-steel-300 hover:text-white"
          @click="openLikesModal"
        >
          Likes
        </button>
        <button
          type="button"
          class="inline-flex items-center gap-2 rounded-full border border-white/10 px-3 py-2 text-xs transition hover:border-white/30 hover:text-white"
          @click="openReportModal"
        >
          Report
        </button>
        <button
          type="button"
          class="inline-flex items-center gap-2 rounded-full border border-white/10 px-3 py-2 text-xs transition hover:border-white/30 hover:text-white"
          @click="sharePost"
        >
          <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M4 12v7a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-7" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M12 3v13" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M8 7l4-4 4 4" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          Share
        </button>
      </div>
      <p v-if="statusMessage" class="mt-2 text-sm text-steel-300">{{ statusMessage }}</p>

      <div class="mt-6 rounded-2xl border border-white/10 bg-midnight-900/40 p-5">
        <p class="text-xs uppercase tracking-[0.3em] text-steel-400">Comments</p>
        <div v-if="comments.length" class="mt-4 space-y-4">
          <div v-for="comment in comments" :key="comment.id" class="text-sm text-steel-200/90">
            <div class="flex flex-col gap-2">
              <div class="min-w-0 flex items-center gap-3">
                <router-link :to="`/u/${comment.authorHandle}`" class="hover:opacity-80 transition flex-shrink-0">
                  <div class="h-10 w-10 overflow-hidden rounded-full border border-white/10 bg-midnight-900/70">
                    <img
                      v-if="comment.authorAvatar"
                      :src="comment.authorAvatar"
                      alt=""
                      class="h-full w-full object-cover"
                      referrerpolicy="no-referrer"
                      crossorigin="anonymous"
                    />
                    <div v-else class="flex h-full w-full items-center justify-center text-xs font-semibold text-steel-200">
                      {{ getInitials(comment.authorName) }}
                    </div>
                  </div>
                </router-link>
                <router-link :to="`/u/${comment.authorHandle}`" class="hover:opacity-80 transition">
                  <p class="font-semibold text-white">{{ comment.authorName }}</p>
                  <p class="text-xs text-steel-400 hover:text-neon-400">@{{ comment.authorHandle }}</p>
                </router-link>
                <p class="text-xs text-steel-400 ml-auto">{{ getRelativeTime(comment.created_at) }}</p>
              </div>
            </div>
            <p class="mt-2">{{ comment.content }}</p>
            <div class="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                class="rounded-full border border-white/10 px-3 py-1 text-[11px] uppercase tracking-[0.3em] text-steel-400 transition hover:text-white"
                @click="comment.replyOpen = !comment.replyOpen"
              >
                Reply
              </button>
              <button
                v-if="canDeleteComment(comment)"
                type="button"
                class="rounded-full border border-rose-200/20 px-3 py-1 text-[11px] uppercase tracking-[0.3em] text-rose-200 transition hover:text-rose-100"
                @click="deleteComment(comment.id)"
              >
                Delete
              </button>
              <div class="inline-flex items-center gap-2 rounded-full border border-white/10 px-3 py-1 text-[11px] uppercase tracking-[0.3em] text-steel-400 transition hover:text-white">
                <button
                  type="button"
                  class="inline-flex items-center gap-2"
                  @click="toggleCommentLike(comment)"
                >
                  <svg
                    class="h-4 w-4"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    aria-hidden="true"
                    :class="comment.isLiked ? 'text-rose-500' : 'text-white'"
                  >
                    <path
                      d="M12 21s-6.7-4.3-9.3-7.6C.5 10.8 1 7.6 3 5.7c2-2 5.2-1.6 7 0l2 1.9 2-1.9c1.8-1.6 5-2 7 0 2 1.9 2.5 5.1.3 7.7C18.7 16.7 12 21 12 21z"
                    />
                  </svg>
                </button>
                <button
                  type="button"
                  class="text-[11px] uppercase tracking-[0.3em] text-steel-300 hover:text-white"
                  @click.stop="openCommentLikesModal(comment)"
                >
                  {{ comment.likeCount }}
                </button>
              </div>
            </div>

            <div
              v-if="comment.replies.length"
              class="mt-3 space-y-3 rounded-2xl border border-white/10 bg-midnight-900/60 p-3"
            >
              <div v-for="reply in comment.replies" :key="reply.id">
                <div class="flex items-center gap-3">
                  <router-link :to="`/u/${reply.authorHandle}`" class="hover:opacity-80 transition flex-shrink-0">
                    <div class="h-8 w-8 overflow-hidden rounded-full border border-white/10 bg-midnight-900/70">
                      <img
                        v-if="reply.authorAvatar"
                        :src="reply.authorAvatar"
                        alt=""
                        class="h-full w-full object-cover"
                        referrerpolicy="no-referrer"
                        crossorigin="anonymous"
                      />
                      <div v-else class="flex h-full w-full items-center justify-center text-[10px] font-semibold text-steel-200">
                        {{ getInitials(reply.authorName) }}
                      </div>
                    </div>
                  </router-link>
                  <div class="flex flex-col gap-2 flex-1">
                    <p class="text-xs text-steel-400">
                      <router-link :to="`/u/${reply.authorHandle}`" class="inline-block hover:opacity-80 transition">
                        <span class="font-semibold text-white hover:text-neon-400">{{ reply.authorName }}</span>
                        <span class="hover:text-neon-400">· @{{ reply.authorHandle }}</span>
                      </router-link>
                      <span class="ml-2">{{ getRelativeTime(reply.created_at) }}</span>
                    </p>
                    <div class="flex flex-wrap gap-2">
                      <button
                        v-if="canDeleteComment(reply)"
                        type="button"
                        class="rounded-full border border-rose-200/20 px-3 py-1 text-[11px] uppercase tracking-[0.3em] text-rose-200 transition hover:text-rose-100"
                        @click="deleteComment(reply.id)"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
                <p class="text-sm text-steel-200/90 mt-2">{{ reply.content }}</p>
              </div>
            </div>

            <form
              v-if="comment.replyOpen"
              class="mt-3 flex flex-col gap-3 sm:flex-row"
              @submit.prevent="addReply(comment)"
            >
              <input
                v-model.trim="comment.newReply"
                type="text"
                placeholder="Write a reply..."
                class="flex-1 rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-sm text-white placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
              />
              <button
                type="submit"
                class="rounded-2xl bg-white px-4 py-3 text-xs font-semibold uppercase tracking-[0.3em] text-midnight-900 transition hover:bg-neon-400"
              >
                Reply
              </button>
            </form>
          </div>
        </div>
        <p v-else class="mt-4 text-sm text-steel-400">No comments yet.</p>

        <form class="mt-4 flex flex-col gap-3 sm:flex-row" @submit.prevent="addComment">
          <input
            v-model.trim="newComment"
            type="text"
            placeholder="Write a comment..."
            class="flex-1 rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-sm text-white placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
          />
          <button
            type="submit"
            class="rounded-2xl bg-white px-4 py-3 text-xs font-semibold uppercase tracking-[0.3em] text-midnight-900 transition hover:bg-neon-400"
          >
            Comment
          </button>
        </form>
      </div>
    </div>

    <p v-if="errorMessage" class="rounded-2xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-100">
      {{ errorMessage }}
    </p>

    <!-- Floating toast notification (shows on mobile too) -->
    <div v-if="statusMessage" class="fixed bottom-6 left-1/2 z-60 -translate-x-1/2 rounded-full bg-neon-400/95 px-4 py-2 text-sm font-semibold text-midnight-900 shadow pointer-events-none">
      {{ statusMessage }}
    </div>

    <div
      v-if="showLikesModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-6"
      @click.self="closeLikesModal"
    >
      <div class="w-full max-w-md rounded-3xl border border-white/10 bg-midnight-900/95 p-6 shadow-glow">
        <div class="flex items-center justify-between">
          <h3 class="font-display text-lg font-semibold text-white">Likes</h3>
          <button
            type="button"
            class="rounded-full border border-white/10 px-3 py-1 text-xs uppercase tracking-[0.3em] text-steel-300 hover:text-white"
            @click="closeLikesModal"
          >
            Close
          </button>
        </div>
        <p v-if="likesLoading" class="mt-4 text-sm text-steel-300">Loading...</p>
        <p v-else-if="likesError" class="mt-4 text-sm text-rose-200">{{ likesError }}</p>
        <p v-else-if="!likesUsers.length" class="mt-4 text-sm text-steel-300">No likes yet.</p>
        <div v-else class="mt-4 space-y-3">
          <router-link
            v-for="user in likesUsers"
            :key="user.id"
            :to="`/u/${user.handle}`"
            class="flex items-center gap-3 rounded-2xl border border-white/10 bg-midnight-900/60 px-3 py-2 hover:border-white/20"
            @click="closeLikesModal"
          >
            <div class="h-10 w-10 overflow-hidden rounded-full border border-white/10 bg-midnight-900/70">
              <img
                v-if="user.avatar"
                :src="user.avatar"
                alt=""
                class="h-full w-full object-cover"
                referrerpolicy="no-referrer"
                crossorigin="anonymous"
              />
              <div v-else class="flex h-full w-full items-center justify-center text-sm font-semibold text-steel-200">
                {{ user.initials }}
              </div>
            </div>
            <div class="min-w-0">
              <p class="truncate text-sm text-white">{{ user.fullName }}</p>
              <p class="text-[10px] uppercase tracking-[0.3em] text-steel-400">@{{ user.handle }}</p>
            </div>
          </router-link>
        </div>
      </div>
    </div>

    <div
      v-if="showCommentLikesModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-6"
      @click.self="closeCommentLikesModal"
    >
      <div class="w-full max-w-md rounded-3xl border border-white/10 bg-midnight-900/95 p-6 shadow-glow">
        <div class="flex items-center justify-between">
          <h3 class="font-display text-lg font-semibold text-white">Comment Likes</h3>
          <button
            type="button"
            class="rounded-full border border-white/10 px-3 py-1 text-xs uppercase tracking-[0.3em] text-steel-300 hover:text-white"
            @click="closeCommentLikesModal"
          >
            Close
          </button>
        </div>
        <p v-if="commentLikesLoading" class="mt-4 text-sm text-steel-300">Loading...</p>
        <p v-else-if="commentLikesError" class="mt-4 text-sm text-rose-200">{{ commentLikesError }}</p>
        <p v-else-if="!commentLikesUsers.length" class="mt-4 text-sm text-steel-300">No likes yet.</p>
        <div v-else class="mt-4 space-y-3">
          <router-link
            v-for="user in commentLikesUsers"
            :key="user.id"
            :to="`/u/${user.handle}`"
            class="flex items-center gap-3 rounded-2xl border border-white/10 bg-midnight-900/60 px-3 py-2 hover:border-white/20"
            @click="closeCommentLikesModal"
          >
            <div class="h-10 w-10 overflow-hidden rounded-full border border-white/10 bg-midnight-900/70">
              <img
                v-if="user.avatar"
                :src="user.avatar"
                alt=""
                class="h-full w-full object-cover"
                referrerpolicy="no-referrer"
                crossorigin="anonymous"
              />
              <div v-else class="flex h-full w-full items-center justify-center text-sm font-semibold text-steel-200">
                {{ user.initials }}
              </div>
            </div>
            <div class="min-w-0">
              <p class="truncate text-sm text-white">{{ user.fullName }}</p>
              <p class="text-[10px] uppercase tracking-[0.3em] text-steel-400">@{{ user.handle }}</p>
            </div>
          </router-link>
        </div>
      </div>
    </div>

    <div
      v-if="showReportModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-6"
      @click.self="closeReportModal"
    >
      <div class="w-full max-w-md rounded-3xl border border-white/10 bg-midnight-900/95 p-6 shadow-glow">
        <div class="flex items-center justify-between">
          <h3 class="font-display text-lg font-semibold text-white">Report post</h3>
          <button
            type="button"
            class="rounded-full border border-white/10 px-3 py-1 text-xs uppercase tracking-[0.3em] text-steel-300 hover:text-white"
            @click="closeReportModal"
          >
            Close
          </button>
        </div>
        <p class="mt-3 text-xs text-steel-400">Tell us what is wrong with this post.</p>
        <textarea
          v-model.trim="reportReason"
          rows="4"
          maxlength="500"
          placeholder="Reason (e.g. nude content, spam, harassment)"
          class="mt-3 w-full resize-none rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-sm text-white placeholder:text-steel-500 focus:border-rose-400/70 focus:outline-none focus:ring-2 focus:ring-rose-400/30"
        ></textarea>
        <div class="mt-3 flex items-center justify-between">
          <p v-if="reportError" class="text-xs text-rose-300">{{ reportError }}</p>
          <p v-if="reportStatus" class="text-xs text-neon-300">{{ reportStatus }}</p>
          <button
            type="button"
            class="ml-auto rounded-full bg-white px-4 py-2 text-[10px] uppercase tracking-[0.3em] text-midnight-900 transition hover:bg-rose-300 disabled:cursor-not-allowed disabled:opacity-70"
            :disabled="reportSubmitting || !reportReason"
            @click="submitReport"
          >
            {{ reportSubmitting ? 'Sending...' : 'Send report' }}
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { supabase, getAvatarUrl } from '@/lib/supabase'

export default {
  name: 'PostView',
  data() {
    return {
      post: null,
      comments: [],
      errorMessage: '',
      newComment: '',
      userId: '',
      profile: {},
      postLikeCount: 0,
      postIsLiked: false,
      showLikesModal: false,
      likesLoading: false,
      likesError: '',
      likesUsers: [],
      showCommentLikesModal: false,
      commentLikesLoading: false,
      commentLikesError: '',
      commentLikesUsers: [],
      showReportModal: false,
      reportReason: '',
      reportSubmitting: false,
      reportError: '',
      reportStatus: '',
      statusMessage: '',
      touchStartX: 0,
      touchEndX: 0,
      touchStartY: 0,
      touchEndY: 0,
      preloadedImages: {},
      mediaDirection: 'next',
    }
  },
  computed: {
    authorName() {
      return this.profile.full_name || 'DRVN Member'
    },
    authorHandle() {
      return this.profile.handle || 'member'
    },
    authorAvatar() {
      return getAvatarUrl({ avatarPath: this.profile.avatar_path, avatarUrl: this.profile.avatar_url })
    },
    authorInitials() {
      return (this.profile.full_name || 'D')
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((chunk) => chunk[0].toUpperCase())
        .join('')
    },
    createdLabel() {
      return this.post?.created_at ? new Date(this.post.created_at).toLocaleDateString() : ''
    },
    postImageUrl() {
      if (!this.post) return ''
      return this.post.mediaUrls?.[this.post.mediaIndex] || this.post.mediaUrls?.[0] || ''
    },
    postImagePosition() {
      if (!this.post?.mediaOffsets || !this.post.mediaOffsets.length) return '50% 50%'
      const index = this.post.mediaIndex || 0
      const offset = this.post.mediaOffsets[index] || { x: 50, y: 50 }
      return `${offset.x}% ${offset.y}%`
    },
  },
  async mounted() {
    const { data } = await supabase.auth.getSession()
    this.userId = data?.session?.user?.id || ''
    await this.fetchPost()
    await this.fetchPostLikes()
    await this.fetchComments()
  },
  methods: {
    async fetchPostLikes() {
      if (!this.post?.id) return
      const { data } = await supabase
        .from('post_likes')
        .select('user_id')
        .eq('post_id', this.post.id)

      this.postLikeCount = data?.length || 0
      this.postIsLiked = Boolean(data?.some((like) => like.user_id === this.userId))
    },
    closeLikesModal() {
      this.showLikesModal = false
      this.likesUsers = []
      this.likesError = ''
      this.likesLoading = false
    },
    async openLikesModal() {
      if (!this.post?.id) return
      this.showLikesModal = true
      this.likesLoading = true
      this.likesError = ''
      this.likesUsers = []

      const { data: likes, error } = await supabase
        .from('post_likes')
        .select('user_id')
        .eq('post_id', this.post.id)

      if (error) {
        this.likesError = 'Unable to load likes.'
        this.likesLoading = false
        return
      }

      const userIds = (likes || []).map((like) => like.user_id).filter(Boolean)
      if (!userIds.length) {
        this.likesUsers = []
        this.likesLoading = false
        return
      }

      const { data: profiles, error: profileError } = await supabase
        .from('profiles')
        .select('id, handle, full_name, avatar_url, avatar_path')
        .in('id', userIds)

      if (profileError) {
        this.likesError = 'Unable to load likes.'
        this.likesLoading = false
        return
      }

      const profileMap = new Map((profiles || []).map((profile) => [profile.id, profile]))

      this.likesUsers = userIds.map((userId) => {
        const profile = profileMap.get(userId) || {}
        const avatar = getAvatarUrl({ avatarPath: profile.avatar_path, avatarUrl: profile.avatar_url })
        const fullName = profile.full_name || 'DRVN Member'
        const handle = profile.handle || 'member'
        const initials = fullName
          .split(' ')
          .filter(Boolean)
          .slice(0, 2)
          .map((chunk) => chunk[0].toUpperCase())
          .join('')
        return {
          id: userId,
          handle,
          fullName,
          avatar,
          initials,
        }
      })
      this.likesLoading = false
    },
    async togglePostLike() {
      if (!this.userId) {
        this.errorMessage = 'Please sign in to like posts.'
        return
      }

      if (!this.post?.id) return

      if (this.postIsLiked) {
        await supabase
          .from('post_likes')
          .delete()
          .eq('post_id', this.post.id)
          .eq('user_id', this.userId)
        this.postIsLiked = false
        this.postLikeCount = Math.max(0, this.postLikeCount - 1)
      } else {
        await supabase.from('post_likes').insert({
          post_id: this.post.id,
          user_id: this.userId,
        })
        this.postIsLiked = true
        this.postLikeCount += 1
        await this.notifyPostOwner('post_like', this.post.id)
      }
    },
    async sharePost() {
      this.errorMessage = ''
      this.statusMessage = ''
      const postUrl = `${window.location.origin}/post/${this.post?.id || ''}`

      const ok = await this.copyTextToClipboard(postUrl)
      if (ok) {
        this.statusMessage = 'Post link copied to clipboard.'
      } else {
        this.errorMessage = 'Unable to copy link. ' + postUrl
      }

      setTimeout(() => (this.statusMessage = ''), 3000)
    },

    async copyTextToClipboard(text) {
      // Prefer modern API
      if (navigator.clipboard && navigator.clipboard.writeText) {
        try {
          await navigator.clipboard.writeText(text)
          return true
        } catch (e) {
          // fall back to legacy method
        }
      }

      try {
        const el = document.createElement('textarea')
        el.value = text
        el.setAttribute('readonly', '')
        el.style.position = 'absolute'
        el.style.left = '-9999px'
        document.body.appendChild(el)

        const selection = document.getSelection()
        const range = selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null
        el.select()
        const success = document.execCommand('copy')
        document.body.removeChild(el)

        if (range && selection) {
          selection.removeAllRanges()
          selection.addRange(range)
        }

        return success
      } catch (e) {
        return false
      }
    },
    getRelativeTime(createdAt) {
      if (!createdAt) return ''
      const now = new Date()
      const created = new Date(createdAt)
      const diffMs = now - created
      const diffMins = Math.floor(diffMs / (1000 * 60))
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

      if (diffMins < 60) return `${diffMins}m`
      if (diffHours < 24) return `${diffHours}h`
      return `${diffDays}d`
    },
    getInitials(name) {
      if (!name) return 'D'
      return name
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((chunk) => chunk[0].toUpperCase())
        .join('')
    },
    preloadMedia(urls, index = 0) {
      if (!urls || !urls.length) return
      const candidates = [index, index + 1, index - 1]
        .filter((i) => i >= 0 && i < urls.length)
        .map((i) => urls[i])
        .filter(Boolean)

      candidates.forEach((url) => {
        if (this.preloadedImages[url]) return
        const img = new Image()
        img.decoding = 'async'
        img.src = url
        this.preloadedImages[url] = true
      })
    },
    setMediaIndex(idx) {
      if (!this.post?.mediaUrls || idx < 0 || idx >= this.post.mediaUrls.length) return
      this.mediaDirection = idx > (this.post.mediaIndex || 0) ? 'next' : 'prev'
      this.post.mediaIndex = idx
      this.preloadMedia(this.post.mediaUrls, this.post.mediaIndex || 0)
    },
    handleSwipeStart(e) {
      this.touchStartX = e.changedTouches[0].clientX
      this.touchStartY = e.changedTouches[0].clientY
    },
    handleSwipeEnd(e) {
      this.touchEndX = e.changedTouches[0].clientX
      this.touchEndY = e.changedTouches[0].clientY
      if (!this.post?.mediaUrls || this.post.mediaUrls.length <= 1) return
      
      const swipeThreshold = 50
      const diffX = this.touchStartX - this.touchEndX
      const diffY = Math.abs(this.touchStartY - this.touchEndY)
      
      // Only navigate carousel if horizontal movement is greater than vertical movement
      if (Math.abs(diffX) > swipeThreshold && Math.abs(diffX) > diffY) {
        if (diffX > 0) {
          this.navigateToNext()
        } else {
          this.navigateToPrevious()
        }
      }
    },
    navigateToNext() {
      if (!this.post?.mediaUrls || this.post.mediaUrls.length <= 1) return
      this.mediaDirection = 'next'
      this.post.mediaIndex = (this.post.mediaIndex || 0) + 1
      if (this.post.mediaIndex >= this.post.mediaUrls.length) {
        this.post.mediaIndex = 0
      }
      this.preloadMedia(this.post.mediaUrls, this.post.mediaIndex || 0)
      this.$forceUpdate()
    },
    navigateToPrevious() {
      if (!this.post?.mediaUrls || this.post.mediaUrls.length <= 1) return
      this.mediaDirection = 'prev'
      this.post.mediaIndex = (this.post.mediaIndex || 1) - 1
      if (this.post.mediaIndex < 0) {
        this.post.mediaIndex = this.post.mediaUrls.length - 1
      }
      this.preloadMedia(this.post.mediaUrls, this.post.mediaIndex || 0)
      this.$forceUpdate()
    },
    canDeleteComment(comment) {
      return Boolean(this.userId && (comment.user_id === this.userId || this.post?.user_id === this.userId))
    },
    async fetchPost() {
      this.errorMessage = ''
      const postId = this.$route.params.id

      const { data, error } = await supabase
        .from('posts')
        .select('id, caption, description, modifications, engine, hp_stock, hp_tuned, year, image_url, image_path, image_offset_x, image_offset_y, created_at, user_id, profiles (handle, full_name, avatar_url, avatar_path), post_media (image_url, image_path, sort_order, offset_x, offset_y)')
        .eq('id', postId)
        .maybeSingle()

      if (error || !data) {
        this.errorMessage = 'Post not found.'
        return
      }

      const mediaItems = [
        {
          url: data.image_path
            ? supabase.storage.from('posts').getPublicUrl(data.image_path).data?.publicUrl
            : data.image_url,
          offset: {
            x: Number.isFinite(data.image_offset_x) ? data.image_offset_x : 50,
            y: Number.isFinite(data.image_offset_y) ? data.image_offset_y : 50,
          },
        },
        ...(data.post_media || []).map((item) => ({
          url: item.image_path
            ? supabase.storage.from('posts').getPublicUrl(item.image_path).data?.publicUrl
            : item.image_url,
          offset: {
            x: Number.isFinite(item.offset_x) ? item.offset_x : 50,
            y: Number.isFinite(item.offset_y) ? item.offset_y : 50,
          },
        })),
      ].filter((item) => Boolean(item.url))

      this.post = {
        ...data,
        mediaUrls: mediaItems.map((item) => item.url),
        mediaOffsets: mediaItems.map((item) => item.offset),
        mediaIndex: 0,
      }
      this.preloadMedia(this.post.mediaUrls, this.post.mediaIndex || 0)
      this.profile = data.profiles || {}
    },
    async fetchComments() {
      const postId = this.$route.params.id
      const { data } = await supabase
        .from('post_comments')
        .select('id, content, created_at, parent_comment_id, user_id, profiles (handle, full_name, avatar_url, avatar_path)')
        .eq('post_id', postId)
        .order('created_at', { ascending: true })

      const comments = (data || []).map((comment) => {
        const avatar = getAvatarUrl({
          avatarPath: comment.profiles?.avatar_path,
          avatarUrl: comment.profiles?.avatar_url,
        })
        return {
          ...comment,
          authorName: comment.profiles?.full_name || 'DRVN Member',
          authorHandle: comment.profiles?.handle || 'member',
          authorAvatar: avatar,
          replies: [],
          replyOpen: false,
          newReply: '',
          likeCount: 0,
          isLiked: false,
        }
      })

      const commentIds = comments.map((comment) => comment.id)
      const { data: likes } = await supabase
        .from('post_comment_likes')
        .select('comment_id, user_id')
        .in('comment_id', commentIds.length ? commentIds : ['00000000-0000-0000-0000-000000000000'])

      const likeMap = new Map()
      ;(likes || []).forEach((like) => {
        likeMap.set(`${like.comment_id}:${like.user_id}`, true)
      })

      comments.forEach((comment) => {
        comment.likeCount = (likes || []).filter((like) => like.comment_id === comment.id).length
        comment.isLiked = likeMap.has(`${comment.id}:${this.userId}`)
      })

      const topLevel = []
      const byId = new Map(comments.map((comment) => [comment.id, comment]))
      comments.forEach((comment) => {
        if (comment.parent_comment_id) {
          const parent = byId.get(comment.parent_comment_id)
          if (parent) {
            parent.replies.push(comment)
          }
        } else {
          topLevel.push(comment)
        }
      })

      this.comments = topLevel
    },
    async addComment() {
      if (!this.userId) {
        this.errorMessage = 'Please sign in to comment.'
        return
      }

      if (!this.newComment) return

      const postId = this.$route.params.id
      const { error } = await supabase.from('post_comments').insert({
        post_id: postId,
        user_id: this.userId,
        content: this.newComment,
      })

      if (error) {
        this.errorMessage = error.message
        return
      }

      this.newComment = ''
      await this.notifyPostOwner('post_comment', postId)
      await this.fetchComments()
    },
    async addReply(parentComment) {
      if (!this.userId) {
        this.errorMessage = 'Please sign in to reply.'
        return
      }

      if (!parentComment.newReply) return

      const postId = this.$route.params.id
      const { error } = await supabase.from('post_comments').insert({
        post_id: postId,
        user_id: this.userId,
        parent_comment_id: parentComment.id,
        content: parentComment.newReply,
      })

      if (error) {
        this.errorMessage = error.message
        return
      }

      parentComment.newReply = ''
      parentComment.replyOpen = false
      await this.notifyCommentOwner(parentComment.id)
      await this.fetchComments()
    },
    async toggleCommentLike(comment) {
      if (!this.userId) {
        this.errorMessage = 'Please sign in to like comments.'
        return
      }

      if (comment.isLiked) {
        await supabase
          .from('post_comment_likes')
          .delete()
          .eq('comment_id', comment.id)
          .eq('user_id', this.userId)
        comment.isLiked = false
        comment.likeCount = Math.max(0, comment.likeCount - 1)
      } else {
        await supabase.from('post_comment_likes').insert({
          comment_id: comment.id,
          user_id: this.userId,
        })
        comment.isLiked = true
        comment.likeCount += 1
        await this.notifyCommentOwner(comment.id, 'comment_like')
      }
    },
    closeCommentLikesModal() {
      this.showCommentLikesModal = false
      this.commentLikesUsers = []
      this.commentLikesError = ''
      this.commentLikesLoading = false
    },
    openReportModal() {
      this.reportReason = ''
      this.reportError = ''
      this.reportStatus = ''
      this.showReportModal = true
    },
    closeReportModal() {
      this.showReportModal = false
      this.reportReason = ''
      this.reportError = ''
      this.reportStatus = ''
      this.reportSubmitting = false
    },
    async submitReport() {
      if (!this.userId) {
        this.reportError = 'Please sign in to report posts.'
        return
      }
      if (!this.post?.id) {
        this.reportError = 'Post not found.'
        return
      }
      const reason = this.reportReason.trim()
      if (!reason) return

      this.reportSubmitting = true
      this.reportError = ''
      this.reportStatus = ''

      const { error } = await supabase.from('post_reports').insert({
        post_id: this.post.id,
        reporter_id: this.userId,
        reason,
      })

      this.reportSubmitting = false

      if (error) {
        this.reportError = error.message || 'Unable to submit report.'
        return
      }

      this.reportStatus = 'Report sent. Thank you.'
      setTimeout(() => this.closeReportModal(), 1200)
    },
    async openCommentLikesModal(comment) {
      if (!comment?.id) return
      this.showCommentLikesModal = true
      this.commentLikesLoading = true
      this.commentLikesError = ''
      this.commentLikesUsers = []

      const { data: likes, error } = await supabase
        .from('post_comment_likes')
        .select('user_id')
        .eq('comment_id', comment.id)

      if (error) {
        this.commentLikesError = 'Unable to load likes.'
        this.commentLikesLoading = false
        return
      }

      const userIds = (likes || []).map((like) => like.user_id).filter(Boolean)
      if (!userIds.length) {
        this.commentLikesUsers = []
        this.commentLikesLoading = false
        return
      }

      const { data: profiles, error: profileError } = await supabase
        .from('profiles')
        .select('id, handle, full_name, avatar_url, avatar_path')
        .in('id', userIds)

      if (profileError) {
        this.commentLikesError = 'Unable to load likes.'
        this.commentLikesLoading = false
        return
      }

      const profileMap = new Map((profiles || []).map((profile) => [profile.id, profile]))

      this.commentLikesUsers = userIds.map((userId) => {
        const profile = profileMap.get(userId) || {}
        const avatar = getAvatarUrl({ avatarPath: profile.avatar_path, avatarUrl: profile.avatar_url })
        const fullName = profile.full_name || 'DRVN Member'
        const handle = profile.handle || 'member'
        const initials = fullName
          .split(' ')
          .filter(Boolean)
          .slice(0, 2)
          .map((chunk) => chunk[0].toUpperCase())
          .join('')
        return {
          id: userId,
          handle,
          fullName,
          avatar,
          initials,
        }
      })
      this.commentLikesLoading = false
    },
    async deleteComment(commentId) {
      const { error } = await supabase.from('post_comments').delete().eq('id', commentId)
      if (error) {
        this.errorMessage = error.message
        return
      }
      await this.fetchComments()
    },
    async notifyPostOwner(type, postId) {
      if (!this.post?.user_id || this.post.user_id === this.userId) return
      await supabase.from('notifications').insert({
        recipient_id: this.post.user_id,
        actor_id: this.userId,
        post_id: postId,
        type,
      })
    },
    async notifyCommentOwner(commentId, type = 'comment_reply') {
      const { data } = await supabase
        .from('post_comments')
        .select('user_id')
        .eq('id', commentId)
        .maybeSingle()

      if (!data?.user_id || data.user_id === this.userId) return

      await supabase.from('notifications').insert({
        recipient_id: data.user_id,
        actor_id: this.userId,
        post_id: this.post?.id,
        comment_id: commentId,
        type,
      })
    },
  },
}
</script>

<style>
.media-swipe-next-enter-active,
.media-swipe-next-leave-active,
.media-swipe-prev-enter-active,
.media-swipe-prev-leave-active {
  transition: opacity 220ms ease, transform 220ms ease;
}

.media-swipe-next-enter-from {
  opacity: 0;
  transform: translateX(10%);
}

.media-swipe-next-leave-to {
  opacity: 0;
  transform: translateX(-10%);
}

.media-swipe-prev-enter-from {
  opacity: 0;
  transform: translateX(-10%);
}

.media-swipe-prev-leave-to {
  opacity: 0;
  transform: translateX(10%);
}
</style>
